// ═══════════════════════════════════════════════════════════════════════
// DashboardContextStrip.tsx — Phase 5-C dashboard context chips
// ═══════════════════════════════════════════════════════════════════════
// Thin row of metadata chips between the page header and
// WhatThisOSIsSolving on the dashboard. Surfaces context the Architect
// captured but the Agentic Operating System didn't previously render: country, primary +
// secondary currencies, and multi-entity scope.
//
// Renders nothing when no signals are present. Each chip self-conditions.
// ═══════════════════════════════════════════════════════════════════════

// Country → flag emoji + primary currency. Hand-maintained for the
// shapes the Architect actually emits; falls back to no flag + no
// currency chip when unknown. Follow-up: tool-taxonomy module could
// own this list.
const COUNTRY_META: Record<string, { flag: string; currency: string }> = {
  Bahrain: { flag: "🇧🇭", currency: "BHD" },
  "Saudi Arabia": { flag: "🇸🇦", currency: "SAR" },
  UAE: { flag: "🇦🇪", currency: "AED" },
  "United Arab Emirates": { flag: "🇦🇪", currency: "AED" },
  Qatar: { flag: "🇶🇦", currency: "QAR" },
  Kuwait: { flag: "🇰🇼", currency: "KWD" },
  Oman: { flag: "🇴🇲", currency: "OMR" },
  Egypt: { flag: "🇪🇬", currency: "EGP" },
  Nigeria: { flag: "🇳🇬", currency: "NGN" },
  Kenya: { flag: "🇰🇪", currency: "KES" },
  "South Africa": { flag: "🇿🇦", currency: "ZAR" },
  Morocco: { flag: "🇲🇦", currency: "MAD" },
  "United Kingdom": { flag: "🇬🇧", currency: "GBP" },
  UK: { flag: "🇬🇧", currency: "GBP" },
  "United States": { flag: "🇺🇸", currency: "USD" },
  USA: { flag: "🇺🇸", currency: "USD" },
  Singapore: { flag: "🇸🇬", currency: "SGD" },
  India: { flag: "🇮🇳", currency: "INR" },
  Pakistan: { flag: "🇵🇰", currency: "PKR" },
  Turkey: { flag: "🇹🇷", currency: "TRY" },
};

interface DashboardContextStripProps {
  multiEntity?: boolean;
  entityCount?: number;
  country?: string;
  currencies?: any[];
  revenue?: { value: number; currency: string } | null;
}

export function DashboardContextStrip({ multiEntity, entityCount, country, currencies, revenue }: DashboardContextStripProps) {
  const trimmedCountry = (country || "").trim();
  const meta = trimmedCountry ? COUNTRY_META[trimmedCountry] : null;
  // Primary currency precedence: stated revenue currency wins over
  // country-derived default. A US consultancy operating from Bahrain
  // should see USD, not BHD.
  const primaryCurrency =
    (revenue && typeof revenue.currency === "string" && revenue.currency.trim()) || meta?.currency || null;
  const secondaryCurrencies = Array.isArray(currencies)
    ? currencies
        .filter((c) => c && typeof c === "object" && typeof c.code === "string" && c.code.trim().length > 0)
        // Dedupe against the primary so "USD ... + USD (billing)" doesn't show.
        .filter((c) => !primaryCurrency || c.code.trim().toUpperCase() !== primaryCurrency.toUpperCase())
        .slice(0, 3)
    : [];
  const showMultiEntity = multiEntity === true && typeof entityCount === "number" && entityCount > 1;

  if (!trimmedCountry && !primaryCurrency && secondaryCurrencies.length === 0 && !showMultiEntity) return null;

  const chipBase = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-50 border border-zinc-200 text-xs text-zinc-700";

  return (
    <div className="flex flex-wrap gap-1.5">
      {trimmedCountry && (
        <span className={chipBase}>
          {meta?.flag ? <span aria-hidden>{meta.flag}</span> : <span aria-hidden>📍</span>}
          <span>{trimmedCountry}</span>
        </span>
      )}
      {primaryCurrency && (
        <span className={chipBase}>
          <span className="font-mono">{primaryCurrency}</span>
        </span>
      )}
      {secondaryCurrencies.map((c, i) => (
        <span key={`${c.code}-${i}`} className={chipBase}>
          <span className="text-zinc-400">+</span>
          <span className="font-mono">{c.code}</span>
          {typeof c.context === "string" && c.context.trim().length > 0 && (
            <span className="text-zinc-500">({c.context.trim()})</span>
          )}
        </span>
      ))}
      {showMultiEntity && (
        <span
          className={`${chipBase} bg-amber-50 border-amber-200 text-amber-800`}
          title="Multi-entity support is in progress — the Agentic Operating System is currently scoped to one entity. Layering in the rest is on the roadmap."
        >
          <span>Showing: Entity 1 of {entityCount} — multi-entity layering in</span>
          <span aria-hidden className="text-amber-500">(?)</span>
        </span>
      )}
    </div>
  );
}
